import React from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Link } from 'react-router-dom';

/**
 * 错误边界组件 - 捕获React组件树中的错误
 * @extends React.Component
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null,
      errorInfo: null 
    };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error,
      errorInfo
    });
    
    // 发送错误到监控服务（生产环境）
    if (import.meta.env.PROD) {
      // 可以在这里集成 Sentry、LogRocket 等错误监控服务
      console.error('Production error:', error, errorInfo);
    } else {
      console.error('Error caught by boundary:', error, errorInfo);
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleReset = () => {
    this.setState({ 
      hasError: false, 
      error: null,
      errorInfo: null 
    });
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-br from-love-cream via-pink-50 to-love-cream">
          <div className="love-card w-full max-w-lg p-8 text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="w-10 h-10 text-red-500" aria-hidden="true" />
            </div>
            
            <h1 className="text-2xl font-bold text-love-dark mb-4">
              出错了！
            </h1>
            
            <p className="text-gray-600 mb-6">
              很抱歉，应用程序遇到了问题。请尝试刷新页面或返回首页。
            </p>

            {/* 开发环境显示错误详情 */}
            {import.meta.env.DEV && this.state.error && (
              <div className="text-left mb-6 p-4 bg-gray-100 rounded-lg overflow-auto max-h-48">
                <p className="text-red-600 font-mono text-sm mb-2">
                  {this.state.error.toString()}
                </p>
                <pre className="text-xs text-gray-600 font-mono">
                  {this.state.errorInfo?.componentStack}
                </pre>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={this.handleReload}
                className="love-button flex items-center justify-center gap-2"
                type="button"
              >
                <RefreshCw size={18} aria-hidden="true" />
                刷新页面
              </button>
              
              <Link
                to="/"
                onClick={this.handleReset}
                className="px-6 py-3 rounded-xl border border-love-rose text-love-rose hover:bg-love-rose hover:text-white transition-colors flex items-center justify-center gap-2"
              >
                <Home size={18} aria-hidden="true" />
                返回首页
              </Link>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
